import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { AppTheme } from './theme';

interface ThemeCtx {
    mode: AppTheme;
    toggle: () => void;
    setMode: (m: AppTheme) => void;
}

const ThemeModeContext = createContext<ThemeCtx>({
    mode: 'light',
    toggle: () => { },
    setMode: () => { },
});

export const useThemeMode = () => useContext(ThemeModeContext);

const STORAGE_KEY = 'eb_theme';

/** Fournisseur de thème global : gère light/dark, persiste le choix,
 *  applique data-theme sur <html> pour les variables CSS. */
export const AppThemeProvider = ({ children }: { children: ReactNode }) => {
    const [mode, setModeState] = useState<AppTheme>(() => {
        const saved = localStorage.getItem(STORAGE_KEY) as AppTheme | null;
        return saved === 'dark' || saved === 'light' ? saved : 'light';
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', mode);
        localStorage.setItem(STORAGE_KEY, mode);
    }, [mode]);

    const setMode = (m: AppTheme) => setModeState(m);
    const toggle = () => setModeState(prev => (prev === 'dark' ? 'light' : 'dark'));

    const ctxValue = useMemo<ThemeCtx>(() => ({ mode, toggle, setMode }), [mode]);

    return (
        <ThemeModeContext.Provider value={ctxValue}>
            {children}
        </ThemeModeContext.Provider>
    );
};

export default AppThemeProvider;
